import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { DateQueryDateTypeEnum } from '../../../api/mentions/enum/date-query-date-type.enum';
import { DateRangeModel } from './date-range.model';
import moment, { Moment } from 'moment-timezone';
import { SearchCriteriaDateQueryModel } from '../../../api/mentions/model/search-criteria-date-query.model';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';

export class DateQueryModel {
    // Relative ranges that span a single day render as one date, not a start–end range.
    // Mirrors DateDisplayComponent.SINGLE_DAY_RELATIVE_TYPES and Chiron's resolver.
    private static readonly SINGLE_DAY_RELATIVE_TYPES: RelativeTimezoneDateTypeEnum[] = [
        RelativeTimezoneDateTypeEnum.TODAY,
        RelativeTimezoneDateTypeEnum.YESTERDAY
    ];

    type: DateQueryTypeEnum;
    dateType?: DateQueryDateTypeEnum;
    relativeTimezoneDateType?: RelativeTimezoneDateTypeEnum;

    // So, 'yesterday' would have the values [48, 24] here.
    relativeStartOffsetH?: number;
    relativeEndOffsetH?: number;

    absoluteDate?: Date;
    absoluteRange?: DateRangeModel;

    timezone?: string;

    public static clone(dateQuery: DateQueryModel): DateQueryModel {
        const clone: DateQueryModel = new DateQueryModel();

        clone.type = dateQuery.type;
        if (dateQuery.dateType) {
            clone.dateType = dateQuery.dateType;
        }
        if (dateQuery.relativeStartOffsetH || dateQuery.relativeStartOffsetH === 0) {
            clone.relativeStartOffsetH = dateQuery.relativeStartOffsetH;
        }
        if (dateQuery.relativeEndOffsetH || dateQuery.relativeEndOffsetH === 0) {
            clone.relativeEndOffsetH = dateQuery.relativeEndOffsetH;
        }
        if (dateQuery.absoluteDate) {
            clone.absoluteDate = dateQuery.absoluteDate;
        }
        if (dateQuery.absoluteRange) {
            clone.absoluteRange = dateQuery.absoluteRange;
        }
        if (dateQuery.timezone) {
            clone.timezone = dateQuery.timezone;
        }

        if (dateQuery.relativeTimezoneDateType) {
            clone.relativeTimezoneDateType = dateQuery.relativeTimezoneDateType;
        }

        return clone;
    }

    /**
     * Converts the (front-end) date query into the API/render shape the backend and the
     * Chiron twig templates expect (`dateType`, `dateRange`, `timeZone`, …). This is the
     * same shape the backend serves to the twig at send time, so the local twig preview
     * renders identically. Returns null for a typeless/empty query.
     */
    public static toSearchCriteria(dateQuery: DateQueryModel): SearchCriteriaDateQueryModel {
        if (!dateQuery?.type) {
            return null;
        }

        const result = new SearchCriteriaDateQueryModel();
        result.dateType = dateQuery.type;
        result.relativeStartOffsetH = dateQuery.relativeStartOffsetH;
        result.relativeEndOffsetH = dateQuery.relativeEndOffsetH;
        if (dateQuery.absoluteRange) {
            result.dateRange = {
                start: new Date(dateQuery.absoluteRange.start).getTime(),
                end: new Date(dateQuery.absoluteRange.end).getTime()
            };
        }
        if (dateQuery.relativeTimezoneDateType) {
            result.relativeTimezoneDateType = dateQuery.relativeTimezoneDateType;
        }
        if (dateQuery.timezone) {
            result.timeZone = dateQuery.timezone;
        }

        return result;
    }

    public static getLast24HoursQuery(): DateQueryModel {
        return this.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_24_HOURS);
    }

    public static getLastXDaysQuery(x: number): DateQueryModel {
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.RELATIVE;
        dateQuery.relativeStartOffsetH = -24 * +x;
        dateQuery.relativeEndOffsetH = 0;

        return dateQuery;
    }

    public static getLast90DaysQuery(): DateQueryModel {
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        dateQuery.absoluteRange = {
            start: moment().subtract(90, 'days').toDate(),
            end: new Date()
        };
        return dateQuery;
    }

    public static getLastXMonthsQuery(x: number): DateQueryModel {
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.RELATIVE;
        dateQuery.relativeStartOffsetH = -730 * +x;
        dateQuery.relativeEndOffsetH = 0;

        return dateQuery;
    }

    public static getRelativeTimezoneQuery(relativeTimezoneDateTypeEnum: RelativeTimezoneDateTypeEnum): DateQueryModel {
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.RELATIVE_TIMEZONE;
        dateQuery.relativeTimezoneDateType = relativeTimezoneDateTypeEnum;

        return dateQuery;
    }

    /**
     * @TODO: This function will be removed when this class can have a constructor
     */
    public static getAllQuery(): DateQueryModel {
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ALL;

        return dateQuery;
    }

    /**
     * @TODO: This function will be removed when this class can have a constructor
     */
    public static getAbsoluteQuery(): DateQueryModel {
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;

        return dateQuery;
    }

    public static getPreviousPeriod(currentPeriod: DateQueryModel): DateQueryModel {
        if (currentPeriod.type === DateQueryTypeEnum.ABSOLUTE) {
            const startTime = moment(currentPeriod.absoluteRange.start);
            const endTime = moment(currentPeriod.absoluteRange.end);
            const diff = moment.duration(endTime.diff(startTime));

            const returnDateQueryModel = new DateQueryModel();
            returnDateQueryModel.type = DateQueryTypeEnum.ABSOLUTE;
            returnDateQueryModel.absoluteRange = {
                start: startTime.subtract(diff).toDate(),
                end: currentPeriod.absoluteRange.start
            };

            return returnDateQueryModel;
        } else if (currentPeriod.type === DateQueryTypeEnum.RELATIVE) {
            const returnDateQueryModel = new DateQueryModel();

            returnDateQueryModel.dateType = currentPeriod.dateType;
            returnDateQueryModel.type = currentPeriod.type;
            returnDateQueryModel.relativeEndOffsetH = currentPeriod.relativeStartOffsetH;
            returnDateQueryModel.relativeStartOffsetH = (currentPeriod.relativeEndOffsetH - currentPeriod.relativeStartOffsetH) * -2;

            return returnDateQueryModel;
        } else if (currentPeriod.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const startTime = this.getStartDateForRelativeTimezone(currentPeriod.relativeTimezoneDateType, currentPeriod.timezone);
            const endTime = this.getEndDateForRelativeTimezone(currentPeriod.relativeTimezoneDateType, currentPeriod.timezone);
            const diff = moment.duration(endTime.diff(startTime));

            const returnDateQueryModel = new DateQueryModel();
            returnDateQueryModel.type = DateQueryTypeEnum.ABSOLUTE;
            returnDateQueryModel.absoluteRange = {
                start: startTime.clone().subtract(diff).toDate(),
                end: startTime.toDate()
            };

            return returnDateQueryModel;
        } else {
            throw new Error('Can\'t generate previous period for all');
        }
    }

    public static getPreviousPeriodSearchCriteriaDateQueryModel(currentPeriod: SearchCriteriaDateQueryModel): SearchCriteriaDateQueryModel {
        if (currentPeriod.dateType === DateQueryTypeEnum.ABSOLUTE) {
            const startTime = moment(currentPeriod.dateRange.start);
            const endTime = moment(currentPeriod.dateRange.end);
            const diff = moment.duration(endTime.diff(startTime));

            const returnDateQueryModel = new SearchCriteriaDateQueryModel();
            returnDateQueryModel.dateType = DateQueryTypeEnum.ABSOLUTE;
            returnDateQueryModel.dateRange = {
                start: +startTime.subtract(diff).toDate(),
                end: currentPeriod.dateRange.start
            };

            return returnDateQueryModel;
        } else if (currentPeriod.dateType === DateQueryTypeEnum.RELATIVE) {
            const returnDateQueryModel = new SearchCriteriaDateQueryModel();

            returnDateQueryModel.dateType = currentPeriod.dateType;
            returnDateQueryModel.relativeEndOffsetH = currentPeriod.relativeStartOffsetH;
            returnDateQueryModel.relativeStartOffsetH = (currentPeriod.relativeEndOffsetH - currentPeriod.relativeStartOffsetH) * -2;

            return returnDateQueryModel;
        } else if (currentPeriod.dateType === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const startTime = this.getStartDateForRelativeTimezone(currentPeriod.relativeTimezoneDateType, currentPeriod.timeZone);
            const endTime = this.getEndDateForRelativeTimezone(currentPeriod.relativeTimezoneDateType, currentPeriod.timeZone);
            const diff = moment.duration(endTime.diff(startTime));

            const returnDateQueryModel = new SearchCriteriaDateQueryModel();
            returnDateQueryModel.dateType = DateQueryTypeEnum.ABSOLUTE;
            returnDateQueryModel.dateRange = {
                start: +startTime.clone().subtract(diff).toDate(),
                end: +startTime.toDate()
            };

            return returnDateQueryModel;
        } else {
            throw new Error('Can\'t generate previous period for all');
        }
    }

    /**
     * Compares two queries, true if equal
     *
     * @ TODO: This should be converted to non-static once the date-filter component has been replaced, needs to be static to keep old code untouched
     * @param a
     * @param b
     * @param checkDateType when false, differing dateTypes (published/harvested/…) still compare equal
     * @param checkTimezone opt-in: most callers match queries against preset options, where a
     *                      user-stamped timezone must not break the match; dirty-state checks pass
     *                      true so a timezone change alone marks the content as modified
     */
    public static equals(a: DateQueryModel, b: DateQueryModel, checkDateType: boolean = true, checkTimezone: boolean = false) {
        if (a == null && b == null) {
            // Both are null, so they're equal
            return true;
        }
        if (a == null || b == null) {
            // One is null but not both, not equal
            return false;
        }
        if (a.type !== b.type) {
            return false;
        }
        // Allows them to both be falsy (i.e. one null, the other undefined)
        if ((a.relativeStartOffsetH || b.relativeStartOffsetH) &&
            a.relativeStartOffsetH !== b.relativeStartOffsetH) {
            return false;
        }
        if ((a.relativeEndOffsetH || b.relativeEndOffsetH) &&
            a.relativeEndOffsetH !== b.relativeEndOffsetH) {
            return false;
        }
        if (a.absoluteDate !== b.absoluteDate) {
            return false;
        }
        if (!!a.absoluteRange !== !!b.absoluteRange) {
            return false;
        }
        if (
            a.absoluteRange
            && (
                // cached models are sometimes JSON.strigified resulting in +a.absoluteRange.end === NaN, need to convert back to Date before equals checking
                +new Date(a.absoluteRange.end) !== +new Date(b.absoluteRange.end) ||
                +new Date(a.absoluteRange.start) !== +new Date(b.absoluteRange.start)
            )
        ) {
            return false;
        }
        if (checkDateType && (a.dateType && b.dateType) && a.dateType !== b.dateType) {
            return false;
        }

        if (a.relativeTimezoneDateType !== b.relativeTimezoneDateType) {
            return false;
        }

        if (checkTimezone && (a.timezone ?? null) !== (b.timezone ?? null)) {
            return false;
        }

        return true;
    }

    public static isInDateQuery(
        timestamp: Date,
        query: DateQueryModel,
        now: Date = new Date(),
        relativeBufferMinutes: number = 0
    ): boolean {
        if (query.type === DateQueryTypeEnum.ALL) {
            return true;
        }

        let start = +now;
        let end = +now;

        if (query.type === DateQueryTypeEnum.ABSOLUTE) {
            if (query.absoluteDate) {
                start = +moment(query.absoluteDate).startOf('day').toDate();
                end = +moment(query.absoluteDate).endOf('day').toDate();
            } else if (query.absoluteRange) {
                start = +moment(query.absoluteRange.start).startOf('day').toDate();
                end = +moment(query.absoluteRange.end).endOf('day').toDate();
            }
        } else if (query.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            start = +this.getStartDateForRelativeTimezone(query.relativeTimezoneDateType, query.timezone);
            end = +this.getEndDateForRelativeTimezone(query.relativeTimezoneDateType, query.timezone);
        } else { // RELATIVE
            // Using add because the offsets are flipped
            start = +moment(now).add(query.relativeStartOffsetH, 'hours').toDate();
            end = +moment(now).add(query.relativeEndOffsetH, 'hours').add(relativeBufferMinutes, 'minutes').toDate();
        }

        return +timestamp > +start && +timestamp < +end;
    }

    public static getTotalHoursInQuery(query: DateQueryModel): number {
        if (!query) {
            return null;
        }

        let hours: number = null;

        switch (query.type) {
            case DateQueryTypeEnum.ABSOLUTE:
                if (query.absoluteDate) {
                    hours = 24;
                } else if (query.absoluteRange) {
                    hours = Math.abs(moment(query.absoluteRange.end).diff(query.absoluteRange.start, 'hours'));
                }
                break;
            case DateQueryTypeEnum.RELATIVE:
                hours = Math.abs(query.relativeEndOffsetH - query.relativeStartOffsetH);
                break;
            case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                hours = this.getRelativeTimezoneDiffAsHours(query.relativeTimezoneDateType, query.timezone);
                break;
            case DateQueryTypeEnum.ALL:
            default:
                hours = Infinity;
        }

        return hours;
    }

    public static getTotalHoursInApiQuery(query: SearchCriteriaDateQueryModel) {
        if (!query) {
            return null;
        }

        let hours: number = null;

        switch (query.dateType) {
            case DateQueryTypeEnum.ABSOLUTE:
                if (query.absoluteDate) {
                    hours = 24;
                } else if (query.dateRange) {
                    hours = Math.abs(moment(query.dateRange.end).diff(query.dateRange.start, 'hours'));
                }
                break;
            case DateQueryTypeEnum.RELATIVE:
                hours = Math.abs(query.relativeEndOffsetH - query.relativeStartOffsetH);
                break;
            case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                hours = this.getRelativeTimezoneDiffAsHours(query.relativeTimezoneDateType, query.timeZone);
                break;
            case DateQueryTypeEnum.ALL:
            default:
                hours = Infinity;
        }

        return hours;
    }

    static makeAbsoluteRange(query: DateQueryModel, timezone?: string): DateQueryModel {
        if (query.absoluteRange && query.absoluteRange.end && query.absoluteRange.start) {
            const clone = DateQueryModel.clone(query);
            if (timezone) {
                clone.timezone = timezone;
            }
            return clone;
        }

        const newQuery = DateQueryModel.getAbsoluteQuery();
        if (timezone) {
            newQuery.timezone = timezone;
        }
        if (query.absoluteDate) {
            const day = timezone ? moment(query.absoluteDate).tz(timezone) : moment(query.absoluteDate);
            newQuery.absoluteRange = {
                start: day.clone().startOf('day').toDate(),
                end: day.clone().endOf('day').toDate()
            };
        } else if (
            (query.relativeStartOffsetH || query.relativeStartOffsetH === 0)
            && (query.relativeEndOffsetH || query.relativeEndOffsetH === 0)
        ) {
            const now = moment();
            const start = now.clone().subtract(Math.abs(query.relativeStartOffsetH), 'hours');
            const end = now.clone().subtract(Math.abs(query.relativeEndOffsetH), 'hours');

            newQuery.absoluteRange = {
                start: start.toDate(),
                end: end.toDate()
            };
        } else if (query.relativeTimezoneDateType) {
            newQuery.absoluteRange = {
                start: DateQueryModel.getStartDateForRelativeTimezone(query.relativeTimezoneDateType, query.timezone).toDate(),
                end: DateQueryModel.getEndDateForRelativeTimezone(query.relativeTimezoneDateType, query.timezone).toDate()
            };
        } else {
            // Date is `ALL` or otherwise not valid
            throw new Error('Date query can not be converted to absolute range.');
        }

        return newQuery;
    }

    public static getStartDateForRelativeTimezone(
        relativeTimezoneDateType: RelativeTimezoneDateTypeEnum,
        timezone?: string,
        referenceDate?: Date | number
    ): Moment {
        const base = referenceDate != null ? moment(referenceDate) : moment();
        const localNow = timezone ? base.tz(timezone) : base;
        let start;
        switch (relativeTimezoneDateType) {
            case RelativeTimezoneDateTypeEnum.TODAY:
                start = localNow.startOf('day');
                break;
            case RelativeTimezoneDateTypeEnum.YESTERDAY:
                start = localNow.subtract(1, 'days').startOf('day');
                break;
            case RelativeTimezoneDateTypeEnum.THIS_WEEK:
                // Shows content from the current week (Starting on Sunday).
                start = localNow.startOf('week');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_WEEK:
                // Shows content from the last week (Starting on Sunday).
                start = localNow.subtract(1, 'weeks').startOf('week');
                break;
            case RelativeTimezoneDateTypeEnum.THIS_MONTH:
                start = localNow.startOf('month');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_MONTH:
                start = localNow.subtract(1, 'months').startOf('month');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_THREE_MONTHS:
                start = localNow.subtract(3, 'months').startOf('month');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_FOUR_MONTHS:
                start = localNow.subtract(4, 'months').startOf('month');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_24_HOURS:
                start = localNow.subtract(24, 'hours').startOf('hour');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_3_DAYS:
                start = localNow.subtract(3, 'days').startOf('day');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_7_DAYS:
                start = localNow.subtract(7, 'days').startOf('day');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_30_DAYS:
                start = localNow.subtract(30, 'days').startOf('day');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_90_DAYS:
                start = localNow.subtract(90, 'days').startOf('day');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_QUARTER: {
                const month = localNow.month();
                const year = localNow.year();
                if (month < 3) {
                    start = localNow.clone().year(year - 1).month(9).date(1).startOf('day');
                } else if (month < 6) {
                    start = localNow.clone().year(year).month(0).date(1).startOf('day');
                } else if (month < 9) {
                    start = localNow.clone().year(year).month(3).date(1).startOf('day');
                } else {
                    start = localNow.clone().year(year).month(6).date(1).startOf('day');
                }
                break;
            }
            case RelativeTimezoneDateTypeEnum.LAST_FOUR_QUARTERS: {
                // Same end as LAST_QUARTER (last completed quarter), but the window starts three
                // quarters earlier so it spans four consecutive quarters (~1 year).
                const month = localNow.month();
                const year = localNow.year();
                if (month < 3) {
                    start = localNow.clone().year(year - 1).month(9).date(1).startOf('day');
                } else if (month < 6) {
                    start = localNow.clone().year(year).month(0).date(1).startOf('day');
                } else if (month < 9) {
                    start = localNow.clone().year(year).month(3).date(1).startOf('day');
                } else {
                    start = localNow.clone().year(year).month(6).date(1).startOf('day');
                }
                start = start.subtract(9, 'months');
                break;
            }
        }

        return start;
    }

    public static getEndDateForRelativeTimezone(
        relativeTimezoneDateType: RelativeTimezoneDateTypeEnum,
        timezone?: string,
        referenceDate?: Date | number
    ): Moment {
        const base = referenceDate != null ? moment(referenceDate) : moment();
        const localNow = timezone ? base.tz(timezone) : base;
        let end;
        switch (relativeTimezoneDateType) {
            case RelativeTimezoneDateTypeEnum.TODAY:
            case RelativeTimezoneDateTypeEnum.THIS_WEEK:
            case RelativeTimezoneDateTypeEnum.THIS_MONTH:
            case RelativeTimezoneDateTypeEnum.LAST_3_DAYS:
            case RelativeTimezoneDateTypeEnum.LAST_7_DAYS:
            case RelativeTimezoneDateTypeEnum.LAST_30_DAYS:
            case RelativeTimezoneDateTypeEnum.LAST_90_DAYS:
            case RelativeTimezoneDateTypeEnum.LAST_24_HOURS:
                end = localNow;
                break;
            case RelativeTimezoneDateTypeEnum.YESTERDAY:
                end = localNow.subtract(1, 'days').endOf('day');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_WEEK:
                end = localNow.subtract(1, 'weeks').endOf('week');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_MONTH:
            case RelativeTimezoneDateTypeEnum.LAST_THREE_MONTHS:
            case RelativeTimezoneDateTypeEnum.LAST_FOUR_MONTHS:
                end = localNow.subtract(1, 'months').endOf('month');
                break;
            case RelativeTimezoneDateTypeEnum.LAST_QUARTER:
            case RelativeTimezoneDateTypeEnum.LAST_FOUR_QUARTERS: {
                // Both end at the last completed quarter; LAST_FOUR_QUARTERS only differs in start.
                const month = localNow.month();
                const year = localNow.year();
                if (month < 3) {
                    end = localNow.clone().year(year - 1).month(11).endOf('month');
                } else if (month < 6) {
                    end = localNow.clone().year(year).month(2).endOf('month');
                } else if (month < 9) {
                    end = localNow.clone().year(year).month(5).endOf('month');
                } else {
                    end = localNow.clone().year(year).month(8).endOf('month');
                }
                break;
            }
        }

        return end;
    }

    public static getRelativeTimezoneDiffAsHours(relativeTimezoneDateType: RelativeTimezoneDateTypeEnum, timezone: string): number {
        const start = this.getStartDateForRelativeTimezone(relativeTimezoneDateType, timezone);
        const end = this.getEndDateForRelativeTimezone(relativeTimezoneDateType, timezone);
        if(start && end) {
            return moment.duration(end.diff(start)).asHours();
        }
    }

    /**
     * Resolves a configured date query (absolute or relative-timezone) into concrete display
     * timestamps for the date-block dateline. Used by the local twig preview so the center
     * panel matches what Chiron renders at send time — this mirrors
     * RenderTaskBalModel.resolveBriefingDateRange in the Chiron repo. Single-day relative
     * types (TODAY / YESTERDAY) resolve to a single date (`single: true`, value in `start`).
     * Returns null for offset-relative / ALL / typeless queries so the block falls back to its
     * single `data.date`.
     */
    public static resolveBriefingDateRange(
        dateQuery: DateQueryModel
    ): { start: number; end: number; single: boolean } | null {
        if (!dateQuery) {
            return null;
        }

        const timezone: string = dateQuery.timezone;
        let start: Moment;
        let end: Moment;
        let single: boolean = false;

        if (
            dateQuery.type === DateQueryTypeEnum.ABSOLUTE &&
            dateQuery.absoluteRange?.start &&
            dateQuery.absoluteRange?.end
        ) {
            start = timezone ? moment(dateQuery.absoluteRange.start).tz(timezone) : moment(dateQuery.absoluteRange.start);
            end = timezone ? moment(dateQuery.absoluteRange.end).tz(timezone) : moment(dateQuery.absoluteRange.end);
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE && dateQuery.relativeTimezoneDateType) {
            const relativeType = dateQuery.relativeTimezoneDateType;
            start = this.getStartDateForRelativeTimezone(relativeType, timezone);
            if (this.SINGLE_DAY_RELATIVE_TYPES.includes(relativeType)) {
                single = true;
                end = start;
            } else {
                end = this.getEndDateForRelativeTimezone(relativeType, timezone);
            }
        }

        if (!start || !end || !start.isValid() || !end.isValid()) {
            return null;
        }

        return {
            start: this.toDisplayTimestamp(start),
            end: this.toDisplayTimestamp(end),
            single
        };
    }

    /**
     * Locks a resolved endpoint to noon UTC of its local calendar date. The block renders only
     * the date (no time), so this keeps the displayed day correct regardless of the timezone the
     * twig date filter formats in — without it, an end-of-period instant (e.g. 23:59 local) could
     * roll into the next day. Mirrors RenderTaskBalModel.toDisplayTimestamp in the Chiron repo.
     */
    private static toDisplayTimestamp(value: Moment): number {
        return Date.UTC(value.year(), value.month(), value.date(), 12, 0, 0);
    }
}
